import { useRef, useState } from 'react';
import type { Cart, Store } from '@/lib/types';
import { CartCard } from '@/components/CartCard';

interface HomeProps {
  store: Store;
  carts: Cart[];
  /** Single-store users never see the switcher; hide the ‹ back to Stores */
  multiStore: boolean;
  avatarInitials: string;
  /** When the cart data was last fetched (ms); 0 before the first fetch */
  updatedAt: number;
  onOpenSwitcher: () => void;
  onOpenCart: (cartIdx: number) => void;
  onToggleMenu: () => void;
  /** Pull-to-refresh gesture: the spinner spins until this resolves */
  onRefresh?: () => Promise<unknown> | void;
}

const PULL_TRIGGER = 55;
const PULL_MAX = 90;
const MIN_SPIN_MS = 600;

export function Home({
  store,
  carts,
  multiStore,
  avatarInitials,
  updatedAt,
  onOpenSwitcher,
  onOpenCart,
  onToggleMenu,
  onRefresh,
}: HomeProps) {
  const [pull, setPull] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const pulling = useRef(false);

  const clientY = (e: React.TouchEvent | React.MouseEvent) =>
    'touches' in e ? e.touches[0].clientY : e.clientY;

  const ptStart = (e: React.TouchEvent | React.MouseEvent) => {
    if (e.currentTarget.scrollTop <= 0 && !refreshing) {
      startY.current = clientY(e);
      pulling.current = true;
    }
  };

  const ptMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!pulling.current || refreshing) return;
    const d = clientY(e) - startY.current;
    if (d > 4) {
      setPull(Math.min(d * 0.5, PULL_MAX));
      setDragging(true);
    }
  };

  const ptEnd = () => {
    if (!pulling.current) return;
    pulling.current = false;
    if (pull > PULL_TRIGGER) {
      setRefreshing(true);
      setPull(PULL_TRIGGER + 1);
      setDragging(false);
      const started = Date.now();
      Promise.resolve(onRefresh?.()).finally(() => {
        const wait = Math.max(0, MIN_SPIN_MS - (Date.now() - started));
        setTimeout(() => {
          setRefreshing(false);
          setPull(0);
        }, wait);
      });
    } else {
      setPull(0);
      setDragging(false);
    }
  };

  return (
    <div
      className="app-scroll h-full select-none px-[18px] pb-10"
      style={{ flex: '0 0 33.3333%' }}
      onTouchStart={ptStart}
      onTouchMove={ptMove}
      onTouchEnd={ptEnd}
      onMouseDown={ptStart}
      onMouseMove={ptMove}
      onMouseUp={ptEnd}
      onMouseLeave={ptEnd}
    >
      <div
        className="flex items-end justify-center overflow-hidden"
        style={{ height: pull, transition: dragging ? 'none' : 'height .25s ease' }}
      >
        <div
          className="mb-3 h-[22px] w-[22px] rounded-full border-[2.5px] border-muted"
          style={{
            borderTopColor: 'var(--sub)',
            opacity: refreshing ? 1 : Math.min(pull / PULL_TRIGGER, 1),
            transform: `rotate(${pull * 3}deg)`,
            animation: refreshing ? 'zeoSpin .8s linear infinite' : 'none',
          }}
        />
      </div>
      <header className="mt-[26px] flex items-center justify-between">
        <div
          onClick={multiStore ? onOpenSwitcher : undefined}
          className={`flex items-center gap-2.5 py-0.5 pr-1 ${multiStore ? 'cursor-pointer' : ''}`}
        >
          {multiStore && <span className="pb-0.5 pl-0.5 text-xl font-bold text-sub">‹</span>}
          <span className="text-[22px] font-extrabold leading-[normal] text-ink">{store.name}</span>
        </div>
        <div
          onClick={onToggleMenu}
          className="flex h-[34px] w-[34px] cursor-pointer items-center justify-center rounded-full bg-chip text-xs font-bold text-chip-text"
        >
          {avatarInitials}
        </div>
      </header>
      <div className="mb-4 mt-1 h-4 px-0.5 text-xs font-semibold text-faint">
        {updatedAt > 0 &&
          `Updated ${new Date(updatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}
      </div>
      <div className="flex flex-col gap-[9px]">
        {carts.map((cart, i) => (
          <CartCard key={`${cart.serial}-${i}`} cart={cart} onClick={() => onOpenCart(i)} />
        ))}
      </div>
    </div>
  );
}
